import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { GraphQLClient, Variables, request } from "graphql-request";

import { resolveUrl } from "../../../config/apiConfig";
import { isPspMode } from "../../../config/pspMode";

export type ExecuteGraphQLRequestParams<TResult, TVariables> = {
  url: string;
  document: TypedDocumentNode<TResult, TVariables> | string;
  variables?: TVariables;
  requestHeaders: Record<string, string>;
  /**
   * Extra `fetch` options (e.g. `credentials`, `mode`, `cache`, `signal`) spread
   * onto the underlying `GraphQLClient`. `method`, `body`, and `headers` are
   * managed by this util and therefore excluded.
   */
  requestInit?: Omit<RequestInit, "method" | "body" | "headers">;
};

/**
 * GraphQL request helper that, in PSP mode, rewrites `url` to the standalone
 * (controlpanel) origin via `resolveUrl`. Any caller-supplied `requestInit`
 * options are spread onto a per-call `GraphQLClient`; credentials default to
 * `"include"` in PSP mode (so PSP host cookies travel with the request) unless
 * the caller overrides them. When neither `requestInit` options nor PSP-mode
 * credentials apply, the standalone `request` is used.
 */
export function executeGraphQLRequest<TResult, TVariables extends Variables>({
  url,
  document,
  variables,
  requestHeaders,
  requestInit,
}: ExecuteGraphQLRequestParams<TResult, TVariables>): Promise<TResult> {
  const finalUrl = resolveUrl(url);
  const credentials = requestInit?.credentials ?? (isPspMode() ? "include" : undefined);
  const clientConfig = { ...requestInit, ...(credentials ? { credentials } : {}) };
  if (Object.keys(clientConfig).length > 0) {
    const client = new GraphQLClient(finalUrl, clientConfig);
    return client.request<TResult>({ document, variables, requestHeaders });
  }
  return request<TResult>({ url: finalUrl, document, variables, requestHeaders });
}
